// Regras puras do painel inicial (sem React, sem rede): normalização das respostas e ordenação por urgência.
// Nada aqui inventa dado: entrada vazia → saída vazia.
import type { StatusTone } from '@giyus/ui';

export type HomeInboxKind = 'task' | 'approval' | 'exception';

export interface HomeInboxItem {
  readonly kind: HomeInboxKind;
  readonly id: string;
  readonly typeCode: string;
  readonly subject: string | null;
  readonly priority: string | null;
  readonly severity: string | null;
  readonly dueAt: string | null;
  readonly overdue: boolean;
  /** `null` = item de rede (sem tenant). */
  readonly tenantId: string | null;
}

const KINDS: ReadonlySet<string> = new Set(['task', 'approval', 'exception']);

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** Aceita o contrato do servidor (lista simples) e o contrato preview (`{ items }`); descarta o que não for item válido. */
export function normalizeInboxItems(raw: unknown): HomeInboxItem[] {
  const list: unknown = Array.isArray(raw)
    ? raw
    : raw !== null && typeof raw === 'object' && Array.isArray((raw as { items?: unknown }).items)
      ? (raw as { items: unknown[] }).items
      : [];
  const out: HomeInboxItem[] = [];
  for (const entry of list as unknown[]) {
    if (entry === null || typeof entry !== 'object') {
      continue;
    }
    const item = entry as Record<string, unknown>;
    const kind = typeof item['kind'] === 'string' ? item['kind'].toLowerCase() : '';
    const id = asString(item['id']);
    if (!KINDS.has(kind) || id === null) {
      continue;
    }
    const scope =
      item['scope'] !== null && typeof item['scope'] === 'object'
        ? (item['scope'] as Record<string, unknown>)
        : {};
    out.push({
      kind: kind as HomeInboxKind,
      id,
      typeCode: asString(item['typeCode']) ?? '',
      subject: asString(item['subject']),
      priority: asString(item['priority']),
      severity: asString(item['severity']),
      dueAt: asString(item['dueAt']),
      overdue: item['overdue'] === true,
      tenantId: asString(scope['tenantId']),
    });
  }
  return out;
}

/** Na home de um tenant: itens do próprio tenant e de rede (sem tenant); nunca de outro tenant. */
export function filterInboxForTenant(
  items: readonly HomeInboxItem[],
  tenantId: string,
): HomeInboxItem[] {
  return items.filter((item) => item.tenantId === null || item.tenantId === tenantId);
}

/** 4 atrasado · 3 crítico/urgente · 2 alto · 1 demais. */
export function inboxRank(item: HomeInboxItem): number {
  if (item.overdue) {
    return 4;
  }
  if (item.severity === 'Critical' || item.priority === 'Urgent') {
    return 3;
  }
  if (item.severity === 'High' || item.priority === 'High') {
    return 2;
  }
  return 1;
}

export function inboxTone(item: HomeInboxItem): StatusTone {
  const rank = inboxRank(item);
  return rank >= 3 ? 'danger' : rank === 2 ? 'warning' : 'info';
}

export interface Rankable {
  readonly rank: number;
  /** ISO; `null` = sem prazo (vai depois dos que têm prazo, no mesmo grau). */
  readonly dueAt: string | null;
}

/** Mais urgente primeiro: grau (desc), depois prazo mais próximo; estável nos empates. */
export function sortByUrgency<T extends Rankable>(items: readonly T[]): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      if (a.item.rank !== b.item.rank) {
        return b.item.rank - a.item.rank;
      }
      if (a.item.dueAt !== b.item.dueAt) {
        if (a.item.dueAt === null) {
          return 1;
        }
        if (b.item.dueAt === null) {
          return -1;
        }
        return a.item.dueAt < b.item.dueAt ? -1 : 1;
      }
      return a.index - b.index;
    })
    .map(({ item }) => item);
}

/** Os contratos tipam `int32` como `number | string`. */
export function toNumber(value: number | string | null | undefined): number {
  const parsed = typeof value === 'number' ? value : Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

export interface CountByStatus {
  readonly status: string;
  readonly count: number | string;
}

/** Só as situações com ocorrências viram fatia da rosca (a legenda não lista zeros). */
export function toSegments(
  counts: readonly CountByStatus[],
  label: (status: string) => string,
): { id: string; label: string; value: number; colorIndex: number }[] {
  // A cor segue a posição da situação na lista completa (antes de remover zeros): a mesma situação mantém a cor.
  return counts
    .map((entry, colorIndex) => ({
      id: entry.status,
      label: label(entry.status),
      value: toNumber(entry.count),
      colorIndex,
    }))
    .filter((segment) => segment.value > 0);
}

export function enrollmentPercent(current: number, target: number): number {
  return target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;
}

/** Dossiês "em andamento" = ainda não assinados. */
export function dossiersInProgress(counts: readonly CountByStatus[]): number {
  return counts
    .filter((entry) => entry.status !== 'Signed')
    .reduce((sum, entry) => sum + toNumber(entry.count), 0);
}
